import { DOCUMENTATION_LANGUAGE, type FieldSpecification, m, type ValueDocumentation } from '@orus.eu/message'
import { failure, type Result, success } from '@orus.eu/result'
import { type Language, translate } from '@orus.eu/translations'

import {
  AbstractDimension,
  type DimensionMcpAdapter,
  type DimensionTrackingMessageFieldAdapter,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  type LooselyTypedValue,
} from './abstract-dimension.ts'

export class BooleanDimension<NAME extends string> extends AbstractDimension<NAME, boolean> {
  override validateData(value: LooselyTypedValue): Result<boolean, DimensionValidationProblem> {
    return typeof value === 'boolean'
      ? success(value)
      : dimensionValidationFailure(`Field ${this.name} is not a boolean`)
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = {
    getTrackingFieldSpecification: (): FieldSpecification => {
      const valueDocumentation: ValueDocumentation<boolean> = {
        title: this.displayNames[DOCUMENTATION_LANGUAGE],
      }
      if (this.hints) {
        valueDocumentation.detailedExplanationMd = this.hints[DOCUMENTATION_LANGUAGE]
      }
      return m.nullable(m.boolean(valueDocumentation))
    },
    convertStateValueToTrackingValue: (stateValue: boolean | null | undefined): boolean | null => {
      if (stateValue == undefined) return null
      return stateValue
    },
  }

  override formatToString(value: boolean, opts: { language: Language }): string | undefined {
    return translate(value ? 'yes' : 'no', opts.language)
  }

  override readonly mcpAdapter: DimensionMcpAdapter<boolean> = {
    parseValueFromMcp(mcpValueInput: string): Result<boolean, string> {
      return mcpValueInput === 'true'
        ? success(true)
        : mcpValueInput === 'false'
          ? success(false)
          : failure(
              `Invalid input format: ${mcpValueInput}. For this tool, you must only use the strings "true" or "false".`,
            )
    },
    formattingInstructions: 'For this tool, you must only use the strings "true" or "false".',
  }
}

export function formatRequiredBooleanForDocuments(value: boolean): string {
  return value ? 'Oui' : 'Non'
}
